import classNames from "classnames";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnvironment } from "../../customHooks/useEnvironment";
import { useLayout } from "../../customHooks/useLayout";
import { decodeUrl } from "../../helperFunctions";
import { useAppSelector } from "../../redux/hooks";
import { ActionCard } from "../cards/actionCard";
import { Icon } from "../icons/icon";

export const OnboardingChecklist = () => {
  const [checklist, setChecklist] = useState<
    {
      svg: string;
      title: string;
      text: string;
      buttonText: string;
      isCompleted: boolean;
      ActionClick: () => void;
      visible: boolean;
    }[]
  >([]);

  const navigate = useNavigate();

  const profile = useAppSelector((state) => {
    return state.auth.profile;
  });

  const onboardingCompletion = useAppSelector((state) => {
    return state.auth.onboardingCompletionStatus;
  });

  const { closePopup } = useLayout();

  const { environment } = useEnvironment();

  useEffect(() => {
    setChecklist([
      {
        svg: "send-colored",
        title: "Getting Started",
        text: `Give us some information about ${
          profile?.type === "cooperative" ? "your cooperative" : "yourself"
        }.`,
        buttonText: onboardingCompletion?.isGetStartedStarted
          ? "Continue"
          : "Start",
        isCompleted: !!onboardingCompletion?.isGetStartedComplete,
        ActionClick: () => {
          navigate("/app/settings/profile/edit");
        },
        visible: true,
      },
      {
        svg: "people-colored",
        title: "Invite cooperative officials",
        text: "Send an invite to the emails of your cooperative officials.",
        buttonText: onboardingCompletion?.isOfficialsStarted
          ? "Continue"
          : "Start",
        isCompleted: !!onboardingCompletion?.isOfficialsComplete,
        ActionClick: () => {
          navigate("/app/settings?tab=manage_officials");
        },
        visible: profile?.type === "cooperative",
      },
      {
        svg: "bank-colored",
        title: `Add ${
          profile?.type === "cooperative" ? "cooperative" : "your"
        } bank details`,
        text: `Fill in your ${
          profile?.type === "cooperative" ? "current cooperative" : ""
        } bank details`,
        buttonText: "Start",
        isCompleted: !!onboardingCompletion?.isBankDetailsComplete,
        ActionClick: () => {
          navigate("/app/settings?tab=bank_account");
        },
        visible: true,
      },
      {
        svg: "document-colored",
        title: "Create membership form",
        text: "Setup the information you need from your members",
        buttonText: "Start",
        isCompleted: !!onboardingCompletion?.isMembershipFormComplete,
        ActionClick: () => {
          navigate("/app/settings?tab=membership_settings");
        },
        visible: profile?.type === "cooperative",
      },
    ]);
    // eslint-disable-next-line
  }, [profile, onboardingCompletion]);

  return (
    <div className="min-h-full w-full flex flex-col">
      {/* Green area */}
      <div className="bg-pry-dark-green-100">
        <div
          className={classNames(
            "border-b border-pry-green-200 bg-pry-dark-green-100 sticky top-0 w-full flex items-center justify-between px-6 md:px-8 py-6",
            {
              "!pt-[calc(44px+24px)] md:!pt-6": environment === "mobile",
            },
          )}
        >
          <p className="text-white text-sm md:text-base font-bold">
            Onboarding Checklist
          </p>

          <button
            onClick={() => {
              closePopup();
            }}
            type="button"
            className={classNames("w-fit", {})}
          >
            <Icon
              svg="cancel-icon"
              width={24}
              height={24}
              className="hover:cursor-pointer text-white"
            />
          </button>
        </div>

        <div
          className="
          px-6 md:px-8 py-6
          text-white flex items-center justify-between
          gap-4
          "
        >
          <div className="flex-1 flex flex-col gap-3 md:gap-4">
            <p className="font-bold text-base md:text-2xl">
              Hello{" "}
              {profile?.type === "cooperative"
                ? profile?.CoopName
                : profile?.FirstName}{" "}
              👋🏼, welcome to ThriftCorp
            </p>

            <div className="flex gap-2 items-center">
              <Icon
                svg="info-icon"
                width={24}
                height={24}
                className="!w-fit text-white hidden md:flex"
              />

              <p className="text-xs md:text-base font-medium">
                Kindly complete the following steps to fully activate your
                account
              </p>
            </div>
          </div>

          <div
            className={`
              bg-light-green-bg w-[93px] h-[93px] md:w-[127px] md:h-[127px]
              rounded-full flex items-center justify-center
              md:px-[18px]
              ${profile?.logo && "!bg-transparent !px-0"}
              cursor-pointer overflow-hidden px-2
              flex flex-col items-center justify-center gap-1
              `}
            onClick={() => {
              navigate("/app/settings?tab=profile");
              closePopup();
            }}
          >
            {profile?.logo || profile?.Picture ? (
              <img
                src={`${decodeUrl(profile?.logo ?? profile?.Picture)}`}
                alt="logo"
                className="w-full h-full object-cover"
              />
            ) : (
              <>
                <Icon
                  svg="gallery-icon-2"
                  height={24}
                  width={24}
                  className="w-[24px] h-[24px] md:w-[32px] md:h-[32px]"
                />

                <p className="text-xs md:text-sm text-pry-green-200 text-center font-medium">
                  {profile?.type === "member"
                    ? "Add Your Picture"
                    : "Add Your Logo"}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* White area */}
      <div
        className="
        bg-white py-6 px-6 md:px-8 flex flex-col gap-[10px] flex-1
        "
      >
        {checklist
          .sort((item1, item2) => {
            if (item1?.isCompleted === item2?.isCompleted) {
              return 0;
            } else if (item1?.isCompleted) {
              return -1;
            } else {
              return 1;
            }
          })
          .map((item, i) => {
            const key = `checklist-option-${i}`;

            return item.visible ? (
              <div
                key={key}
                className={!item.isCompleted ? "min-h-[108px]" : ""}
              >
                <ActionCard
                  svg={item.svg}
                  title={item.title}
                  text={item.text}
                  buttonText={item.buttonText}
                  isCompleted={item.isCompleted}
                  className={!item.isCompleted ? "!h-full" : ""}
                  onActionClick={item.ActionClick}
                />
              </div>
            ) : undefined;
          })}
      </div>
    </div>
  );
};
